import { inject, observer } from "mobx-react";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Modal from "./modal";
import { fetch } from "../utils/api-wrapper";
import { loadXpensCategories, getXpensCategoryLabel } from "../utils/xpens-categories";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Utils, { getSelectStyles } from "../utils/utils";
import { imputationDocumentId } from "../pages/review-expense/utils/contract-expense-json";
import { getImputationModalValidationErrors } from "../utils/expenses-imputations-modal-validation";
import {
  applyCategoryTranslationSync,
  imputationRowHasParentId,
  mapApiChildToSubcategory,
  normalizeDepartmentIdsFromApi,
  normalizeRankIdsFromApi,
  sortXpensCategoriesByDisplayLabel,
} from "../utils/expenses-imputations-config-helpers";
import { SELECTION_CHIP_LIST_SPACING } from "../utils/selection-chip-styles";
import { buildMultiSelectPickerParts } from "./multi-select-chip-picker";

const GL_NATURALS_PAGE_SIZE = 200;
const IMPUTATION_TABLE_SORTING = encodeURIComponent(JSON.stringify([{ id: "category", desc: false }]));

const REQUIRED_FIELD_MARK = <span className="text-danger" aria-hidden="true"> *</span>;

// Defined at module scope so React never sees it as a new component on re-render.
// Defining it inside the component body causes inputs inside sections to lose focus on every keystroke.
const ModalSection = ({ title, children }) => (
  <div className="card p-3 mb-3">
    <h6 className="mb-3">{title}</h6>
    {children}
  </div>
);

const EMPTY_FORM = (idClient) => ({
  category: "",
  origin: "client",
  maps_from_verify_category: null,
  default_expense_type: null,
  gl_natural: "",
  gl_office: "",
  id_client: idClient,
  cost_type: "",
  tax_code: null,
  approver_user_ids: [],
  department_ids: [],
  rank_ids: [],
  client_filter: null,
});

/**
 * Reusable create/edit modal for imputation configuration.
 *
 * Accepts `initial` for edit mode: `{ parentRow, isParentMode, focusedChildId }`.
 * When `imputationCatalog` is provided ("expense" | "displacement"), a read-only
 * Type field is shown — used by T006/T007 to distinguish catalog context.
 *
 * @param {object}   props.data               - MobX store
 * @param {object}   [props.initial]          - null = new; object = edit
 * @param {string}   [props.imputationCatalog] - null | "expense" | "displacement"
 * @param {Array}    props.approvableUsers     - [{value, label}] from host
 * @param {Array}    props.departmentOptions   - [{value, label}] from host
 * @param {Array}    props.rankOptions         - [{value, label}] from host
 * @param {Function} props.onSuccess           - (message: string) => void
 * @param {Function} props.onError             - (message: string) => void
 * @param {Function} props.onClose             - () => void
 */
const ImputationConfigModal = ({
  data,
  initial,
  imputationCatalog,
  approvableUsers,
  departmentOptions,
  rankOptions,
  onSuccess,
  onError,
  onClose,
}) => {
  // Lazy initialisers — run synchronously on first render, no useEffect race.
  // The modal is conditionally rendered so fresh mount = fresh open every time.
  const [updatedImputation, setUpdatedImputation] = useState(() => {
    if (!initial) return EMPTY_FORM(data.id_client);
    const { parentRow } = initial;
    return {
      ...parentRow,
      maps_from_verify_category: parentRow.maps_from_verify_category ?? null,
      default_expense_type: parentRow.default_expense_type ?? null,
      client_filter: parentRow.client_filter ?? null,
      approver_user_ids: parentRow.approver_user_ids ?? [],
      department_ids: normalizeDepartmentIdsFromApi(parentRow),
      rank_ids: imputationRowHasParentId(parentRow)
        ? []
        : normalizeRankIdsFromApi(parentRow),
    };
  });
  const [isParentMode, setIsParentMode] = useState(() => initial?.isParentMode ?? false);
  const [subcategories, setSubcategories] = useState([]);
  const [deletedSubcategoryIds, setDeletedSubcategoryIds] = useState([]);
  // focusedChildId is fixed for the lifetime of the modal (re-mounts per open).
  const focusedSubcategoryId = initial?.focusedChildId ?? null;
  const [imputationFormErrors, setImputationFormErrors] = useState([]);
  const [glNaturalOptions, setGlNaturalOptions] = useState([]);
  const glNaturalStartRef = useRef(0);
  const glNaturalTotalRef = useRef(0);
  const glNaturalLoadingRef = useRef(false);
  // Tracks mount state so in-flight fetch callbacks don't update state after unmount.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  // Children fetch for edit/parent mode — needs a useEffect because it is async.
  useEffect(() => {
    if (!initial?.isParentMode) return;
    const impId = imputationDocumentId(initial.parentRow);
    if (!impId) return;
    const searchData = `?filters=${encodeURIComponent(JSON.stringify([{ id: "parent_id", value: impId }]))}&sorting=${IMPUTATION_TABLE_SORTING}&start=0&page_size=100`;
    fetch(`/imputations/${searchData}`, "GET", {}, (response) => {
      if (isMountedRef.current) {
        setSubcategories((response?.data?.imputations ?? []).map(mapApiChildToSubcategory));
      }
    });
  }, []); // mount only

  // XPEN-3187: the linkage dropdown needs the real category list to
  // function at all — unlike categoryLabel's other callers, a failed
  // fetch here must surface a visible error, not just fall back silently.
  useEffect(() => {
    loadXpensCategories(data, onError);
  }, [data, onError]);

  // Scroll focused subcategory into view when it becomes visible
  useEffect(() => {
    if (!focusedSubcategoryId || subcategories.length === 0) return undefined;
    const idx = subcategories.findIndex((sub) => sub._id === focusedSubcategoryId);
    if (idx < 0) return undefined;
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(`imputation-subcategory-panel-${idx}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [focusedSubcategoryId, subcategories]);

  const readLabel = useCallback(
    (key, fallback) => {
      const value = data.t.read(key);
      return typeof value === "string" && value.trim() !== "" ? value : fallback;
    },
    [data.t]
  );

  const hasAderantIntegration = Utils.hasAderantIntegration(data);
  const canEdit = Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]);

  const categoryLabel = useCallback(
    (category) => getXpensCategoryLabel(data, category),
    [data]
  );

  const taxCodeOptions = useMemo(
    () => (data.config?.tax_profile ?? []).map((t) => ({ label: t.code, value: t.code })),
    [data.config]
  );

  const expenseTypeOptions = useMemo(() => [
    { value: "client", label: readLabel("review_expense.expense_type.client", "Client") },
    { value: "internal", label: readLabel("review_expense.expense_type.internal", "Non-client") },
  ], [readLabel]);

  // XPEN-3251: value is the display name (name_en), not the code. The XPEN-3187
  // PR review chose the code specifically so a future rename of name_en would
  // not orphan an already-saved maps_from_verify_category — but every reader of
  // that field (the AI extraction's Category, the migration's repaired links,
  // the backend's exact-match lookup, approval rules) already keys on name_en,
  // so a code-format link never matched anything. Renaming a standard category
  // now breaks existing links and needs a repair patch — the same cost the
  // migration already carries, and the accepted trade-off for this fix.
  // getXpensCategoryLabel still matches on code, name_en, or name_fr, so a
  // link saved before this change (holding a code) still displays correctly.
  // XPEN-3686 (XPEN-3435-001): alphabetical in the displayed language, so
  // data.t.lang is a dependency — a language switch replaces data.t and must
  // re-sort.
  const verifyMapOptions = useMemo(
    () =>
      sortXpensCategoriesByDisplayLabel(data.xpensCategories, data).map((c) => ({
        value: c.name_en,
        label: categoryLabel(c.name_en),
      })),
    // `data` is a MobX observable that keeps its identity; its fields are the
    // real dependencies, which the lint rule cannot see.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, data.xpensCategories, data.t.lang, categoryLabel]
  );

  const mapsVerifyValue = useMemo(() => {
    const m = updatedImputation.maps_from_verify_category;
    if (m == null || m === "") return null;
    return { value: m, label: categoryLabel(m) };
  }, [updatedImputation.maps_from_verify_category, categoryLabel, data.xpensCategories]);

  const originLabel = useCallback(
    (origin) => {
      if (origin === "verify_default") return data.t.read("imputation_origin_verify_default");
      if (origin === "client") return data.t.read("imputation_origin_client");
      return origin ?? "";
    },
    [data.t]
  );

  // Derived label maps from host-provided option arrays
  const departmentIdToLabel = useMemo(() => {
    const map = {};
    departmentOptions.forEach((d) => { map[d.value] = d.label; });
    return map;
  }, [departmentOptions]);

  const rankIdToLabel = useMemo(() => {
    const map = {};
    rankOptions.forEach((r) => { map[r.value] = r.label; });
    return map;
  }, [rankOptions]);

  // GL Natural async loader with ref-based pagination to avoid stale closures
  const loadGlNaturals = useCallback(
    (search = "", callback = () => {}) => {
      const filters = [{ id: "id_client", value: data.id_client }];
      if (search) filters.push({ id: "GLNatural", value: search });
      const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify([{ id: "GLNatural", desc: false }]))}&start=0&page_size=${GL_NATURALS_PAGE_SIZE}`;
      fetch(`/gl_naturals/${searchData}`, "GET", {}, (response) => {
        const items = (response?.data?.gl_naturals ?? []).map((g) => ({
          label: g.GLNatural,
          value: g.GLNatural,
        }));
        if (!search && isMountedRef.current) {
          glNaturalTotalRef.current = response?.data?.meta?.totalRowCount ?? items.length;
          glNaturalStartRef.current = items.length;
          setGlNaturalOptions(items);
        }
        callback(items);
      });
    },
    [data.id_client]
  );

  const handleGlNaturalScrollToBottom = useCallback(() => {
    if (glNaturalLoadingRef.current || glNaturalStartRef.current >= glNaturalTotalRef.current) return;
    glNaturalLoadingRef.current = true;
    const filters = [{ id: "id_client", value: data.id_client }];
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify([{ id: "GLNatural", desc: false }]))}&start=${glNaturalStartRef.current}&page_size=${GL_NATURALS_PAGE_SIZE}`;
    fetch(`/gl_naturals/${searchData}`, "GET", {}, (response) => {
      const newItems = (response?.data?.gl_naturals ?? []).map((g) => ({
        label: g.GLNatural,
        value: g.GLNatural,
      }));
      glNaturalStartRef.current += newItems.length;
      glNaturalLoadingRef.current = false;
      if (isMountedRef.current) setGlNaturalOptions((prev) => [...prev, ...newItems]);
    });
  }, [data.id_client]);

  const loadGlOffices = useCallback(
    (search = "", callback = () => {}) => {
      const val = (search || "").trim().replace(/\//g, "");
      const filters = [{ id: "search", value: val }, { id: "id_client", value: data.id_client }];
      const sorting = [{ id: "name", desc: false }];
      const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(sorting))}`;
      fetch(`/offices/${searchData}`, "GET", {}, (response) => {
        callback((response?.data?.offices ?? []).map((o) => ({ label: o.name, value: o.name })));
      });
    },
    [data.id_client]
  );

  const loadCostTypesAsync = useCallback(
    (search = "", callback = () => {}) => {
      const filters = [{ id: "id_client", value: data.id_client }];
      if (search) filters.push({ id: "search", value: search });
      const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=0&page_size=50`;
      fetch(`/cost_types/${searchData}`, "GET", {}, (response) => {
        callback(
          (response?.data?.cost_types ?? []).map((c) => ({
            label: c.cost_type ? `${c.cost_type}${c.description ? " - " + c.description : ""}` : "",
            value: c.cost_type,
          }))
        );
      });
    },
    [data.id_client]
  );

  // CRUD operations
  const createImputation = useCallback(() => {
    const fd = new FormData();
    const payload = applyCategoryTranslationSync({ ...updatedImputation }, updatedImputation);
    delete payload._id;
    // Stamp the catalog explicitly so the API record matches this page's context.
    if (imputationCatalog) payload.imputation_catalog = imputationCatalog;
    // Displacement imputations never carry department/rank scoping.
    if (imputationCatalog === "displacement") {
      delete payload.department_ids;
      delete payload.rank_ids;
    }
    fd.append("imputation", JSON.stringify(payload));
    fetch(
      "/imputations/",
      "POST",
      fd,
      () => onSuccess(data.t.read("imputation_created_successfully")),
      () => onError(data.t.read("imputation_creation_failure"))
    );
  }, [updatedImputation, data.t, onSuccess, onError, imputationCatalog]);

  const updateImputation = useCallback(() => {
    const impId = imputationDocumentId(updatedImputation);
    const payload = applyCategoryTranslationSync({ ...updatedImputation }, updatedImputation);
    delete payload._id;
    // Displacement imputations never carry department/rank scoping.
    if (imputationCatalog === "displacement") {
      delete payload.department_ids;
      delete payload.rank_ids;
    }
    const fd = new FormData();
    fd.append("imputation", JSON.stringify(payload));
    fetch(
      `/imputations/${impId}`,
      "PUT",
      fd,
      () => onSuccess(data.t.read("update_successful")),
      () => onError(data.t.read("failed_to_update"))
    );
  }, [updatedImputation, data.t, onSuccess, onError]);

  const submitParentWithChildren = useCallback(() => {
    const isUpdate = !!imputationDocumentId(updatedImputation);

    const parentPayload = applyCategoryTranslationSync(
      {
        category: updatedImputation.category,
        origin: updatedImputation.origin || "client",
        id_client: data.id_client,
        // Displacement imputations never carry department/rank scoping.
        ...(imputationCatalog !== "displacement" ? {
          default_expense_type: updatedImputation.default_expense_type ?? null,
          department_ids: Array.isArray(updatedImputation.department_ids) ? updatedImputation.department_ids : [],
          rank_ids: Array.isArray(updatedImputation.rank_ids) ? updatedImputation.rank_ids : [],
        } : {}),
        ...(imputationCatalog ? { imputation_catalog: imputationCatalog } : {}),
      },
      updatedImputation
    );

    const buildChildren = (parentId) =>
      subcategories.map((sub) =>
        applyCategoryTranslationSync(
          {
            ...(sub._id ? { _id: sub._id } : {}),
            category: sub.category,
            gl_natural: sub.gl_natural || "",
            gl_office: sub.gl_office || "",
            cost_type: sub.cost_type || "",
            tax_code: sub.tax_code ?? null,
            maps_from_verify_category: sub.maps_from_verify_category ?? null,
            ...(imputationCatalog !== "displacement" ? {
              client_filter: sub.client_filter ?? null,
              department_ids: sub.department_ids ?? [],
              rank_ids: sub.rank_ids ?? [],
            } : {}),
            approver_user_ids: sub.approver_user_ids ?? [],
            parent_id: parentId,
            origin: updatedImputation.origin || "client",
            id_client: data.id_client,
            ...(imputationCatalog ? { imputation_catalog: imputationCatalog } : {}),
          },
          sub
        )
      );

    const successMsg = data.t.read(isUpdate ? "update_successful" : "imputation_created_successfully");

    const onBulkSuccess = () => onSuccess(successMsg);
    const onBulkError = (e) => {
      const code = e?.response?.data?.error;
      if (code === "imputation_bulk_write_failed") {
        onError(data.t.read("imputation_children_partial_failure"));
      } else {
        onError(data.t.read(isUpdate ? "failed_to_update" : "imputation_creation_failure"));
      }
    };

    const sendBulk = (parentId) => {
      fetch(
        `/imputations/${parentId}?with_children=true`,
        "PUT",
        {
          parent: parentPayload,
          children: buildChildren(parentId),
          deleted_child_ids: deletedSubcategoryIds,
        },
        onBulkSuccess,
        onBulkError
      );
    };

    if (isUpdate) {
      sendBulk(imputationDocumentId(updatedImputation));
    } else {
      const fd = new FormData();
      fd.append("imputation", JSON.stringify(parentPayload));
      fetch(
        "/imputations/",
        "POST",
        fd,
        (response) => {
          const newId =
            response?.data?._id?.$oid ||
            response?.data?._id ||
            response?.data?.id ||
            response?.data?.imputation_id;
          if (!newId) {
            onError(data.t.read("imputation_creation_failure"));
            return;
          }
          const hasChildren = subcategories.length > 0 || deletedSubcategoryIds.length > 0;
          if (hasChildren) {
            sendBulk(String(newId));
          } else {
            onBulkSuccess();
          }
        },
        () => onError(data.t.read("imputation_creation_failure"))
      );
    }
  }, [updatedImputation, subcategories, deletedSubcategoryIds, data, onSuccess, onError, imputationCatalog]);

  const handleImputationSubmit = (e) => {
    e.preventDefault();
    const nextErrors = getImputationModalValidationErrors(readLabel, {
      updatedImputation,
      isParentMode,
      subcategories,
    });
    if (nextErrors.length > 0) {
      setImputationFormErrors(nextErrors);
      return;
    }
    setImputationFormErrors([]);
    // Close before API call — matching existing behavior
    onClose();
    if (isParentMode) {
      submitParentWithChildren();
    } else if (imputationDocumentId(updatedImputation)) {
      updateImputation();
    } else {
      createImputation();
    }
  };

  const renderApproverUserIdsPicker = useCallback(
    ({ inputId, selectedIds, onChangeIds, chipTestId }) =>
      buildMultiSelectPickerParts({
        inputId,
        selectedIds,
        onChangeIds,
        options: approvableUsers,
        resolveLabel: (id) => approvableUsers.find((u) => u.value === id)?.label || id,
        placeholder: data.t.read("imputation_approvers_placeholder"),
        chipTestId,
        editAllowed: canEdit,
        removeLabel: data.t.read("delete"),
      }),
    [approvableUsers, data.t, canEdit]
  );

  const renderDepartmentIdsPicker = useCallback(
    ({ inputId, selectedIds, onChangeIds, chipTestId }) =>
      buildMultiSelectPickerParts({
        inputId,
        selectedIds,
        onChangeIds,
        options: departmentOptions,
        resolveLabel: (id) => departmentIdToLabel[id] || id,
        placeholder: data.t.read("imputation_departments_placeholder"),
        chipTestId,
        editAllowed: canEdit,
        removeLabel: data.t.read("delete"),
      }),
    [departmentOptions, departmentIdToLabel, data, canEdit]
  );

  const renderRankIdsPicker = useCallback(
    ({ inputId, selectedIds, onChangeIds, chipTestId }) =>
      buildMultiSelectPickerParts({
        inputId,
        selectedIds,
        onChangeIds,
        options: rankOptions,
        resolveLabel: (id) => rankIdToLabel[id] || id,
        placeholder: data.t.read("imputation_ranks_placeholder"),
        chipTestId,
        editAllowed: canEdit,
        removeLabel: data.t.read("delete"),
      }),
    [rankOptions, rankIdToLabel, data, canEdit]
  );

  const renderModalField = (fieldName, labelKey, controlOrParts, { requiredMark = false, labelFor, helperText } = {}) => {
    const control = controlOrParts?.control ?? controlOrParts;
    const selectionList = controlOrParts?.selectionList ?? null;
    return (
      <div key={fieldName} className="mb-4 d-flex align-items-start">
        <label className="col-4 col-form-label pt-2" htmlFor={labelFor}>
          {data.t.read(labelKey)}
          {requiredMark ? REQUIRED_FIELD_MARK : null}
        </label>
        <div className="col-8">
          {control}
          {helperText ? (
            <small className="form-text text-muted d-block mb-0" style={{ marginTop: SELECTION_CHIP_LIST_SPACING }}>
              {helperText}
            </small>
          ) : null}
          {selectionList}
        </div>
      </div>
    );
  };

  return (
    <form method="post" onSubmit={handleImputationSubmit}>
      <Modal
        title="Imputation"
        okIsSubmit={true}
        cancelAction={onClose}
        okText={data.t.read("save")}
        cancelText={data.t.read("cancel")}
      >
        {imputationFormErrors.length > 0 && (
          <div className="alert alert-danger mb-3" role="alert" data-testid="imputation-form-validation-errors">
            <ul className="mb-0 pl-3">
              {imputationFormErrors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <ModalSection title={data.t.read("imputation_section_identity")}>
          {renderModalField("category", "category", (
            <input
              id="imputation-modal-category"
              data-testid="imputation-modal-category-input"
              className="form-control"
              type="text"
              readOnly={!canEdit || updatedImputation.default_category === true}
              value={
                updatedImputation.default_category === true
                  ? categoryLabel(updatedImputation.category)
                  : (updatedImputation.category || "")
              }
              onChange={(e) => {
                setImputationFormErrors([]);
                setUpdatedImputation((prev) => ({ ...prev, category: e.target.value }));
              }}
            />
          ), { requiredMark: true, labelFor: "imputation-modal-category" })}

          {renderModalField("origin", "imputation_origin", (
            <input
              className="form-control"
              type="text"
              readOnly
              value={originLabel(updatedImputation.origin || "client")}
            />
          ))}

          {imputationCatalog != null && renderModalField("catalog_type", "imputation_type", (
            <input
              className="form-control"
              type="text"
              readOnly
              value={data.t.read(`imputation_catalog_${imputationCatalog}`)}
            />
          ))}

          {imputationCatalog !== "displacement" && renderModalField("default_expense_type", "imputation_default_expense_type", (
            <Select
              classNamePrefix="select"
              isDisabled={!canEdit}
              value={updatedImputation.default_expense_type ? { value: updatedImputation.default_expense_type, label: readLabel(`review_expense.expense_type.${updatedImputation.default_expense_type}`, updatedImputation.default_expense_type) } : null}
              options={expenseTypeOptions}
              onChange={(choice) => setUpdatedImputation({ ...updatedImputation, default_expense_type: choice ? choice.value : null })}
              styles={getSelectStyles(false, false)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              placeholder={data.t.read("imputation_default_expense_type_placeholder")}
              isClearable
            />
          ))}

          <div className="mb-4 d-flex align-items-center">
            <label className="col-4">{data.t.read("imputation_has_subcategories")}</label>
            <div className="col-8">
              <input
                type="checkbox"
                role="switch"
                checked={isParentMode}
                disabled={!canEdit}
                onChange={() => {
                  setImputationFormErrors([]);
                  setIsParentMode((v) => !v);
                }}
              />
            </div>
          </div>
        </ModalSection>

        {!isParentMode && (
          <>
            <ModalSection title={data.t.read("imputation_section_accounting")}>
              {renderModalField("gl_natural", "gl_natural", (
                <AsyncSelect
                  classNamePrefix="select"
                  isDisabled={!canEdit}
                  value={updatedImputation.gl_natural ? { label: updatedImputation.gl_natural, value: updatedImputation.gl_natural } : null}
                  loadOptions={loadGlNaturals}
                  defaultOptions={glNaturalOptions}
                  onMenuOpen={() => loadGlNaturals("", () => {})}
                  onMenuScrollToBottom={handleGlNaturalScrollToBottom}
                  isClearable
                  onChange={(choice) => setUpdatedImputation({ ...updatedImputation, gl_natural: choice?.value ?? "" })}
                  styles={getSelectStyles(false, false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              ))}
              {renderModalField("gl_office", "gl_office", (
                <AsyncSelect
                  classNamePrefix="select"
                  isDisabled={!canEdit}
                  value={updatedImputation.gl_office ? { label: updatedImputation.gl_office, value: updatedImputation.gl_office } : null}
                  loadOptions={loadGlOffices}
                  defaultOptions
                  isClearable
                  onChange={(choice) => setUpdatedImputation({ ...updatedImputation, gl_office: choice?.value ?? "" })}
                  styles={getSelectStyles(false, false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              ))}
              {renderModalField("cost_type", "cost_type", (
                <AsyncSelect
                  classNamePrefix="select"
                  isDisabled={!canEdit}
                  value={updatedImputation.cost_type ? { label: updatedImputation.cost_type, value: updatedImputation.cost_type } : null}
                  loadOptions={loadCostTypesAsync}
                  defaultOptions
                  isClearable
                  onChange={(choice) => setUpdatedImputation({ ...updatedImputation, cost_type: choice?.value ?? "" })}
                  styles={getSelectStyles(false, false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              ))}
              {renderModalField("tax_code", "tax", (
                <Select
                  classNamePrefix="select"
                  isDisabled={!canEdit}
                  value={updatedImputation.tax_code ? { label: updatedImputation.tax_code, value: updatedImputation.tax_code } : null}
                  options={taxCodeOptions}
                  onChange={(choice) => setUpdatedImputation({ ...updatedImputation, tax_code: choice ? choice.value : null })}
                  styles={getSelectStyles(false, false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  placeholder={data.t.read("tax")}
                  isClearable
                />
              ))}
              {renderModalField("maps_from_verify_category", "imputation_maps_verify_category", (
                <Select
                  classNamePrefix="select"
                  isDisabled={!canEdit}
                  value={mapsVerifyValue}
                  options={verifyMapOptions}
                  onChange={(choice) => setUpdatedImputation({ ...updatedImputation, maps_from_verify_category: choice ? choice.value : null })}
                  styles={getSelectStyles(false, false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  placeholder={data.t.read("imputation_maps_verify_category")}
                  isClearable
                />
              ))}
            </ModalSection>

            <ModalSection title={data.t.read("imputation_section_access")}>
              {imputationCatalog !== "displacement" && renderModalField(
                "department_ids",
                "imputation_departments",
                renderDepartmentIdsPicker({
                  inputId: "imputation-departments-parent",
                  selectedIds: updatedImputation.department_ids,
                  onChangeIds: (nextIds) => setUpdatedImputation({ ...updatedImputation, department_ids: nextIds }),
                  chipTestId: "imputation-departments-parent-chips",
                })
              )}
              {imputationCatalog !== "displacement" && hasAderantIntegration && renderModalField(
                "rank_ids",
                "imputation_ranks",
                renderRankIdsPicker({
                  inputId: "imputation-ranks-parent",
                  selectedIds: updatedImputation.rank_ids,
                  onChangeIds: (nextIds) => setUpdatedImputation({ ...updatedImputation, rank_ids: nextIds }),
                  chipTestId: "imputation-ranks-parent-chips",
                }),
                { helperText: data.t.read("imputation_ranks_helper") }
              )}
              {renderModalField("approver_user_ids", "imputation_approvers", renderApproverUserIdsPicker({
                inputId: "imputation-approvers-parent",
                selectedIds: updatedImputation.approver_user_ids,
                onChangeIds: (nextIds) => setUpdatedImputation({ ...updatedImputation, approver_user_ids: nextIds }),
                chipTestId: "imputation-approvers-parent-chips",
              }))}
            </ModalSection>
          </>
        )}

        {isParentMode && (
          <div className="mt-2">
            <p className="font-weight-bold mb-2">{data.t.read("imputation_add_subcategory")}</p>
            {subcategories.map((sub, idx) => (
              <div
                key={sub._id || `sub-${idx}`}
                id={`imputation-subcategory-panel-${idx}`}
                className={`border rounded p-2 mb-2${focusedSubcategoryId && sub._id === focusedSubcategoryId ? " border-primary" : ""}`}
                data-testid={focusedSubcategoryId && sub._id === focusedSubcategoryId ? "imputation-subcategory-panel-focused" : undefined}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="font-weight-bold small">{data.t.read("imputation_subcategory_number")} {idx + 1}</span>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => {
                        if (sub._id) setDeletedSubcategoryIds((prev) => [...prev, sub._id]);
                        setSubcategories((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small" htmlFor={`imputation-subcategory-name-${idx}`}>
                    {data.t.read("imputation_subcategory_number")}
                    {REQUIRED_FIELD_MARK}
                  </label>
                  <div className="col-8">
                    <input
                      id={`imputation-subcategory-name-${idx}`}
                      data-testid={`imputation-subcategory-input-${idx}`}
                      className="form-control form-control-sm"
                      type="text"
                      readOnly={!canEdit}
                      value={sub.category}
                      onChange={(e) => {
                        setImputationFormErrors([]);
                        setSubcategories((prev) => prev.map((s, i) => (i === idx ? { ...s, category: e.target.value } : s)));
                      }}
                    />
                  </div>
                </div>
                {imputationCatalog !== "displacement" && (
                  <div className="mb-2 d-flex align-items-center">
                    <label className="col-4 mb-0 small">{data.t.read("imputation_client_filter")}</label>
                    <div className="col-8">
                      <Select
                        classNamePrefix="select"
                        isDisabled={!canEdit}
                        value={sub.client_filter ? { value: sub.client_filter, label: readLabel(`review_expense.expense_type.${sub.client_filter}`, sub.client_filter) } : null}
                        options={expenseTypeOptions}
                        onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, client_filter: choice ? choice.value : null } : s))}
                        styles={getSelectStyles(false, false)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        placeholder={data.t.read("imputation_client_filter_placeholder")}
                        isClearable
                      />
                    </div>
                  </div>
                )}
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small">{data.t.read("gl_natural")}</label>
                  <div className="col-8">
                    <AsyncSelect
                      classNamePrefix="select"
                      isDisabled={!canEdit}
                      value={sub.gl_natural ? { label: sub.gl_natural, value: sub.gl_natural } : null}
                      loadOptions={loadGlNaturals}
                      defaultOptions={glNaturalOptions}
                      onMenuOpen={() => loadGlNaturals("", () => {})}
                      onMenuScrollToBottom={handleGlNaturalScrollToBottom}
                      isClearable
                      onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, gl_natural: choice?.value ?? "" } : s))}
                      styles={getSelectStyles(false, false)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small">{data.t.read("gl_office")}</label>
                  <div className="col-8">
                    <AsyncSelect
                      classNamePrefix="select"
                      isDisabled={!canEdit}
                      value={sub.gl_office ? { label: sub.gl_office, value: sub.gl_office } : null}
                      loadOptions={loadGlOffices}
                      defaultOptions
                      isClearable
                      onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, gl_office: choice?.value ?? "" } : s))}
                      styles={getSelectStyles(false, false)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small">{data.t.read("cost_type")}</label>
                  <div className="col-8">
                    <AsyncSelect
                      classNamePrefix="select"
                      isDisabled={!canEdit}
                      value={sub.cost_type ? { label: sub.cost_type, value: sub.cost_type } : null}
                      loadOptions={loadCostTypesAsync}
                      defaultOptions
                      isClearable
                      onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, cost_type: choice?.value ?? "" } : s))}
                      styles={getSelectStyles(false, false)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small">{data.t.read("tax")}</label>
                  <div className="col-8">
                    <Select
                      classNamePrefix="select"
                      isDisabled={!canEdit}
                      value={sub.tax_code ? { label: sub.tax_code, value: sub.tax_code } : null}
                      options={taxCodeOptions}
                      onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, tax_code: choice?.value ?? null } : s))}
                      styles={getSelectStyles(false, false)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isClearable
                    />
                  </div>
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <label className="col-4 mb-0 small">{data.t.read("imputation_maps_verify_category")}</label>
                  <div className="col-8">
                    <Select
                      classNamePrefix="select"
                      isDisabled={!canEdit}
                      value={sub.maps_from_verify_category ? { label: categoryLabel(sub.maps_from_verify_category), value: sub.maps_from_verify_category } : null}
                      options={verifyMapOptions}
                      onChange={(choice) => setSubcategories((prev) => prev.map((s, i) => i === idx ? { ...s, maps_from_verify_category: choice?.value ?? null } : s))}
                      styles={getSelectStyles(false, false)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isClearable
                    />
                  </div>
                </div>
                <div className="mb-2 d-flex align-items-start">
                  <label className="col-4 mb-0 small pt-2">{data.t.read("imputation_approvers")}</label>
                  <div className="col-8">
                    {(() => {
                      const picker = renderApproverUserIdsPicker({
                        inputId: `imputation-approvers-sub-${idx}`,
                        selectedIds: sub.approver_user_ids,
                        onChangeIds: (nextIds) =>
                          setSubcategories((prev) => prev.map((s, i) => (i === idx ? { ...s, approver_user_ids: nextIds } : s))),
                        chipTestId: `imputation-approvers-sub-${idx}-chips`,
                      });
                      return (
                        <>
                          {picker.control}
                          {picker.selectionList}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {imputationCatalog !== "displacement" && (() => {
                  const deptPicker = renderDepartmentIdsPicker({
                    inputId: `imputation-departments-sub-${idx}`,
                    selectedIds: sub.department_ids,
                    onChangeIds: (nextIds) =>
                      setSubcategories((prev) => prev.map((s, i) => (i === idx ? { ...s, department_ids: nextIds } : s))),
                    chipTestId: `imputation-departments-sub-${idx}-chips`,
                  });
                  return (
                    <div className="mb-2 d-flex align-items-start">
                      <label className="col-4 mb-0 small pt-2">{data.t.read("imputation_departments")}</label>
                      <div className="col-8">
                        {deptPicker.control}
                        {deptPicker.selectionList}
                      </div>
                    </div>
                  );
                })()}
                {imputationCatalog !== "displacement" && hasAderantIntegration && (() => {
                  const rankPicker = renderRankIdsPicker({
                    inputId: `imputation-ranks-sub-${idx}`,
                    selectedIds: sub.rank_ids,
                    onChangeIds: (nextIds) =>
                      setSubcategories((prev) => prev.map((s, i) => (i === idx ? { ...s, rank_ids: nextIds } : s))),
                    chipTestId: `imputation-ranks-sub-${idx}-chips`,
                  });
                  return (
                    <div className="mb-2 d-flex align-items-start">
                      <label className="col-4 mb-0 small pt-2">{data.t.read("imputation_ranks")}</label>
                      <div className="col-8">
                        {rankPicker.control}
                        {rankPicker.selectionList}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
            {canEdit && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                onClick={() =>
                  setSubcategories((prev) => [
                    ...prev,
                    { _id: null, category: "", gl_natural: "", gl_office: "", cost_type: "", tax_code: null, maps_from_verify_category: null, client_filter: null, approver_user_ids: [], department_ids: [], rank_ids: [] },
                  ])
                }
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                {data.t.read("imputation_add_subcategory")}
              </button>
            )}
          </div>
        )}
      </Modal>
    </form>
  );
};

export default inject("data")(observer(ImputationConfigModal));
